// src/Components/VitStraAI.jsx
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  memo,
  useDeferredValue,
  useLayoutEffect,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { interpretUserQuery } from "./AIBrain";
import { FaVideo, FaPlayCircle, FaBars, FaTimes } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./VitStraAI.css";

const advanceGreeting = (fromPath) => {
  const stage = Number(localStorage.getItem("vitstra_greeting_stage") || "0");
  let next = stage;
  if (stage === 0) next = 1;
  else if (fromPath === "/videos") next = 2;
  else if (fromPath === "/short") next = 1;
  localStorage.setItem("vitstra_greeting_stage", String(next));
};

/* ------------------------------------------------------------------ */
/* Placeholder rotation */
/* ------------------------------------------------------------------ */
const PLACEHOLDERS = [
  "Try: I've to become businessman",
  "Try: Show me Tech content",
  "Try: Show psychology content",
  "Try: I'm interested in AI",
];

/* ------------------------------------------------------------------ */
/* Local-storage helpers */
/* ------------------------------------------------------------------ */
const STORAGE_KEY = "vitstra_ai_chat_v2";
const CATEGORY_KEY = "vitstraCategory";
const CUSTOMS_KEY = "vitstraCustoms";

const safeParse = (raw) => {
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
};

const loadCustoms = () => {
  const raw = localStorage.getItem(CUSTOMS_KEY);
  const parsed = safeParse(raw);
  if (Array.isArray(parsed)) return parsed.map(c => c.toLowerCase());
  if (typeof parsed === "string") return [parsed.toLowerCase()];
  return [];
};

const saveCustoms = (arr) => {
  try {
    localStorage.setItem(CUSTOMS_KEY, JSON.stringify(arr.map(c => c.toLowerCase())));
  } catch {}
};

const saveChat = (arr) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch {}
};

const loadChat = () => {
  const r = localStorage.getItem(STORAGE_KEY);
  return r ? JSON.parse(r) : [];
};

/* Message-ID generator */
let messageIdCounter = 0;
const generateId = () => `${Date.now()}_${++messageIdCounter}`;

/* AI processing queue */
const AI_QUEUE = [];
let isProcessing = false;

const processQueue = async () => {
  if (isProcessing || AI_QUEUE.length === 0) return;
  isProcessing = true;

  const {
    messages,
    setMessages,
    availableCategories,
    controller,
    setIsStreaming,
    triggerFeedUpdate,
    setTransitioningAiId,
  } = AI_QUEUE.shift();

  let accumulated = "";
  const aiId = generateId();
  setIsStreaming(true);
  setTransitioningAiId(aiId);

  setMessages(prev => [
    ...prev,
    { id: aiId, from: "ai", text: "", isThinking: true, isStreaming: true },
  ]);

  try {
    const result = await interpretUserQuery(
      messages[messages.length - 1].text,
      availableCategories,
      messages.slice(0, -1),
      "Neutral",
      (chunk) => {
        if (controller.signal.aborted) return;
        accumulated += chunk;
        setMessages(prev =>
          prev.map(m =>
            m.id === aiId ? { ...m, text: accumulated, isThinking: false } : m
          )
        );
      },
      controller.signal
    );

    const finalText = result.response || accumulated;
    let showFeedBtn = false;
    let showFeedbackBtn = false;

    if (result.action === "show" && result.categories) {
      triggerFeedUpdate(result.categories);
      showFeedBtn = true;
    } else if (result.action === "reset") {
      triggerFeedUpdate(["all"]);
      showFeedBtn = true;
    }

    if (messages[messages.length - 1].text.toLowerCase().includes("feedback")) {
      showFeedbackBtn = true;
    }

    setTimeout(() => setTransitioningAiId(null), 800);

    setMessages(prev =>
      prev.map(m =>
        m.id === aiId
          ? {
              ...m,
              text: finalText,
              showFeedBtn,
              showFeedbackBtn,
              isStreaming: false,
              isThinking: false,
            }
          : m
      )
    );
  } catch (err) {
    if (!controller.signal.aborted) {
      console.error("AI Error:", err);
      setMessages(prev =>
        prev.map(m =>
          m.id === aiId
            ? {
                ...m,
                text: "Sorry, I couldn't process that. Try **Show AI** or **Reset feed**.",
                isStreaming: false,
                isThinking: false,
              }
            : m
        )
      );
    } else {
      setMessages(prev => prev.filter(m => m.id !== aiId));
    }
  } finally {
    setIsStreaming(false);
    isProcessing = false;
    processQueue();
  }
};

/* MAIN COMPONENT */
export default function VitStraAI() {
  const navigate = useNavigate();
  const location = useLocation();

  /* ---------- state ---------- */
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);
  const [transitioningAiId, setTransitioningAiId] = useState(null);
  const placeholderInterval = useRef(null);
  const [input, setInput] = useState("");
  const deferredInput = useDeferredValue(input);
  const [messages, setMessages] = useState(() => loadChat());
  const [panelOpen, setPanelOpen] = useState(false);
  const [customs, setCustoms] = useState(() => loadCustoms());
  const [currentCategory, setCurrentCategory] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(CATEGORY_KEY) || '"all"');
      const arr = Array.isArray(s) ? s : [s];
      return arr.map(c => c.toLowerCase());
    } catch {
      return ["all"];
    }
  });
  const [availableCategories, setAvailableCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [setShowScrollDownBtn] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    msgId: null,
    text: "",
    isUser: false,
  });
  const [isStreaming, setIsStreaming] = useState(false);

  /* ---------- refs ---------- */
  const abortControllerRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const wasAtBottomRef = useRef(true);
  const scrollPositionRef = useRef(0);
  const messagesRef = useRef(messages);
  const rafRef = useRef(null);
  const panelOpenRef = useRef(panelOpen);

  useLayoutEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useLayoutEffect(() => {
    panelOpenRef.current = panelOpen;
  }, [panelOpen]);

  /* ---------- greeting sync ---------- */
  useEffect(() => {
    const prev = sessionStorage.getItem("vitstra_last_path");
    if (prev && (prev === "/videos" || prev === "/short")) {
      advanceGreeting(prev);
    }
    sessionStorage.setItem("vitstra_last_path", location.pathname);
  }, [location.pathname]);

  /* ---------- placeholder rotation ---------- */
  useEffect(() => {
    if (inputFocused) {
      if (placeholderInterval.current) clearInterval(placeholderInterval.current);
      return;
    }
    placeholderInterval.current = setInterval(() => {
      setPlaceholderIdx(i => (i + 1) % PLACEHOLDERS.length);
    }, 4500);
    return () => {
      if (placeholderInterval.current) clearInterval(placeholderInterval.current);
    };
  }, [inputFocused]);

  /* ---------- load categories ---------- */
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsLoadingCategories(true);
      try {
        const lang = localStorage.getItem("userLanguage") || "English";
        const langs = lang === "Both" ? ["English", "Hindi"] : [lang];
        const set = new Set();
        for (const l of langs) {
          const r = await fetch(`/API/Shorts/${l}Shorts.json`);
          if (!r.ok) continue;
          const d = await r.json();
          if (d && typeof d === "object") {
            Object.keys(d).forEach(k => {
              if (k.toLowerCase() !== "all") set.add(k.toLowerCase());
            });
          }
        }
        if (mounted) {
          setAvailableCategories(Array.from(set));
          setIsLoadingCategories(false);
        }
      } catch (e) {
        console.error("Failed to load categories:", e);
        if (mounted) {
          setAvailableCategories([
            "ai", "tech", "business", "geopolitics", "psychology", "knowledge"
          ]);
          setIsLoadingCategories(false);
        }
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  /* ---------- preserve scroll when panel opens/closes ---------- */
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const inputContainer = document.querySelector(".input-orb-container");
    if (panelOpen) {
      scrollPositionRef.current = container.scrollTop;
      if (inputContainer) inputContainer.style.opacity = "0";
    } else {
      requestAnimationFrame(() => {
        container.scrollTop = scrollPositionRef.current;
        if (inputContainer) inputContainer.style.opacity = "1";
      });
    }
  }, [panelOpen]);

  /* ---------- pause iframe when panel opens ---------- */
  useEffect(() => {
    if (!panelOpen) return;
    const iframe = document.querySelector('iframe[src*="youtube.com/embed"]');
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } catch (e) {
        console.warn("Could not pause iframe:", e);
      }
    }
  }, [panelOpen]);

  /* ---------- feed update helper ---------- */
  const triggerFeedUpdate = useCallback(
    (cats) => {
      const norm = Array.isArray(cats)
        ? cats.map(c => c.toLowerCase())
        : [String(cats).toLowerCase()];
      
      localStorage.setItem(CATEGORY_KEY, JSON.stringify(norm));
      
      window.dispatchEvent(
        new CustomEvent("vitstra-update", { 
          detail: { 
            category: norm,
            forceReload: true,
            timestamp: Date.now()
          } 
        })
      );
      
      setCustoms(prev => {
        const set = new Set(prev);
        norm.forEach(n => {
          if (n !== "all") set.add(n);
        });
        const next = Array.from(set);
        saveCustoms(next);
        return next;
      });
    },
    []
  );

  /* ---------- stop streaming ---------- */
  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setMessages(prev => prev.filter(m => !m.isStreaming));
  }, []);

  /* ---------- scroll to bottom ---------- */
  const scrollToBottom = useCallback(() => {
    const c = containerRef.current;
    if (c) c.scrollTo({ top: c.scrollHeight, behavior: "smooth" });
  }, []);

  /* ---------- edit message ---------- */
  const startEdit = useCallback((id, text) => {
    setEditingId(id);
    setInput(text);
    setContextMenu({ show: false });
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const saveEdit = useCallback(() => {
    if (!editingId || !input.trim()) return;
    const edited = input.trim();
    const newId = generateId();
    setMessages(prev => {
      const idx = prev.findIndex(m => m.id === editingId);
      if (idx === -1) return prev;
      const copy = [...prev];
      copy[idx] = { ...copy[idx], id: newId, text: edited };
      const aiStart = idx + 1;
      const userEnd = copy.slice(aiStart).findIndex(m => m.from === "user");
      const sliceEnd = userEnd === -1 ? copy.length : aiStart + userEnd;
      copy.splice(aiStart, sliceEnd - aiStart);
      return copy;
    });
    setEditingId(null);
    setInput("");
    setTimeout(() => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      AI_QUEUE.push({
        messages: messagesRef.current.map(m =>
          m.id === editingId ? { ...m, id: newId, text: edited } : m
        ),
        setMessages,
        availableCategories,
        controller,
        setIsStreaming,
        triggerFeedUpdate,
        setTransitioningAiId,
      });
      processQueue();
    }, 100);
  }, [
    editingId,
    input,
    availableCategories,
    triggerFeedUpdate,
    setTransitioningAiId,
  ]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setInput("");
  }, []);

  /* ---------- copy message ---------- */
  const copyMessage = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setContextMenu({ show: false });
  }, []);

  /* ---------- context menu ---------- */
  const handleContextMenu = useCallback((e, msg) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      msgId: msg.id,
      text: msg.text,
      isUser: msg.from === "user",
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu({ show: false });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (contextMenu.show && !e.target.closest(".msg-context-menu")) {
        closeContextMenu();
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [contextMenu.show, closeContextMenu]);

  /* ---------- send message ---------- */
  const sendMessage = useCallback(
    (txt) => {
      if (!txt?.trim() || isLoadingCategories) return;
      const userText = txt.trim();
      const userMsg = { id: generateId(), from: "user", text: userText };
      const cur = messagesRef.current;
      setMessages(m => [...m, userMsg]);
      const controller = new AbortController();
      abortControllerRef.current = controller;
      AI_QUEUE.push({
        messages: [...cur, userMsg],
        setMessages,
        availableCategories:
          availableCategories.length > 0 ? availableCategories : ["ai"],
        controller,
        setIsStreaming,
        triggerFeedUpdate,
        setTransitioningAiId,
      });
      processQueue();
      setInput("");
    },
    [
      availableCategories,
      isLoadingCategories,
      triggerFeedUpdate,
      setTransitioningAiId,
    ]
  );

  /* ---------- global tag click ---------- */
  useEffect(() => {
    window.handleTagClick = (cat) => {
      sendMessage(`Show ${cat} content`);
    };
    return () => {
      delete window.handleTagClick;
    };
  }, [sendMessage]);

  /* ---------- listen for external category changes ---------- */
  useEffect(() => {
    const listener = () => {
      try {
        const s = JSON.parse(localStorage.getItem(CATEGORY_KEY) || '"all"');
        const arr = Array.isArray(s) ? s : [s];
        setCurrentCategory(arr.map(c => c.toLowerCase()));
      } catch {
        setCurrentCategory(["all"]);
      }
    };
    window.addEventListener("vitstra-update", listener);
    return () => window.removeEventListener("vitstra-update", listener);
  }, []);

  /* ---------- persist chat & customs ---------- */
  useEffect(() => saveChat(messages), [messages]);
  useEffect(() => saveCustoms(customs), [customs]);

  /* ---------- scroll handling ---------- */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const check = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const atBottom = scrollHeight - scrollTop - clientHeight < 120;
        const scrolledUp = scrollTop > 300;
        wasAtBottomRef.current = atBottom;
        setShowScrollBtn(scrolledUp && !atBottom);
        setShowScrollDownBtn(!atBottom);
        rafRef.current = null;
      });
    };
    const observer = new ResizeObserver(check);
    observer.observe(container);
    container.addEventListener("scroll", check);
    check();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      container.removeEventListener("scroll", check);
    };
  }, [setShowScrollDownBtn]);

  useEffect(() => {
    if (wasAtBottomRef.current) scrollToBottom();
  }, [messages, scrollToBottom]);

  /* ---------- navigation helpers ---------- */
  const goToVideos = useCallback(() => navigate("/videos"), [navigate]);
  const goToShorts = useCallback(() => navigate("/short"), [navigate]);

  /* ---------- feedback ---------- */
  const handleFeedback = useCallback(() => {
    window.location.href = "/feedback";
  }, []);

  /* ---------- saved-customisation toggle ---------- */
  const applySavedCustomization = useCallback(
    (c) => {
      const lower = c.toLowerCase();
      const active = currentCategory.includes(lower);
      let newCats = active
        ? currentCategory.filter(x => x !== lower)
        : [...currentCategory.filter(x => x !== "all"), lower];
      const final = newCats.length === 0 ? ["all"] : newCats;
      
      localStorage.setItem(CATEGORY_KEY, JSON.stringify(final));
      
      window.dispatchEvent(
        new CustomEvent("vitstra-update", { 
          detail: { 
            category: final,
            forceReload: true,
            timestamp: Date.now()
          } 
        })
      );
      
      setCurrentCategory(final);
    },
    [currentCategory]
  );

  /* ---------- panel open/close ---------- */
  const togglePanel = useCallback(() => setPanelOpen(p => !p), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target.classList.contains("panel-overlay")) closePanel();
    },
    [closePanel]
  );

  /* ---------- form submit ---------- */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) saveEdit();
    else sendMessage(deferredInput);
  };

  const lastMsg = messages[messages.length - 1];
  const showFeedButton = lastMsg?.from === "ai" && lastMsg?.showFeedBtn;

  const popularCategories = ["ai", "tech", "psychology", "geopolitics", "business", "knowledge"];

  return (
    <div className="vitstra-ai-root">
      <div className="vitstra-ai-page">
        {/* ---------- Header ---------- */}
        <header className="vitstra-ai-header">
          <button className="menu-toggle" onClick={togglePanel} aria-label="Menu">
            {panelOpen ? <FaTimes /> : <FaBars />}
          </button>
          <h1 className="title">
            <span className="gradient-text">VitStra Customisation AI</span>
          </h1>
          <button className="custom-btn desktop-only" onClick={togglePanel}>
            Customise
          </button>
        </header>

        {/* ---------- Customisation Panel ---------- */}
        <div className={`vitstra-ai-panel ${panelOpen ? "open" : ""}`}>
          <div className="panel-title-wrapper">
            <div className="panel-header">
              <h2>My Customisations</h2>
              <button
                className="panel-close-btn"
                onClick={closePanel}
                aria-label="Close panel"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <line x1="18" y1="6" x2="6" y2="18" stroke="current" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
          <div className="panel-body">
            <div className="current-status">
              Current:{" "}
              <strong>
                {currentCategory.length === 1 && currentCategory[0] === "all"
                  ? "All"
                  : currentCategory
                      .map(c => c[0].toUpperCase() + c.slice(1))
                      .join(", ")}
              </strong>
            </div>
            <h3>Saved</h3>
            <div className="saved-list">
              {customs.length === 0 ? (
                <p className="no-saved">No saved preferences.</p>
              ) : (
                customs.map(c => (
                  <SavedBtn
                    key={c}
                    label={c[0].toUpperCase() + c.slice(1)}
                    active={currentCategory.includes(c)}
                    onClick={() => applySavedCustomization(c)}
                  />
                ))
              )}
            </div>
            <button
              className="saved-btn reset-all"
              onClick={() => {
                localStorage.setItem(CATEGORY_KEY, JSON.stringify(["all"]));
                window.dispatchEvent(
                  new CustomEvent("vitstra-update", {
                    detail: { 
                      category: ["all"],
                      forceReload: true,
                      timestamp: Date.now()
                    },
                  })
                );
                setCurrentCategory(["all"]);
                const user = {
                  id: generateId(),
                  from: "user",
                  text: "Reset feed",
                };
                const ai = {
                  id: generateId(),
                  from: "ai",
                  text: "Feed reset – all.",
                  showFeedBtn: true,
                };
                setMessages(m => [...m, user, ai]);
              }}
            >
              Reset to All
            </button>
          </div>
        </div>

        {/* ---------- Backdrop ---------- */}
        {panelOpen && (
          <div className="panel-overlay" onClick={handleBackdropClick} />
        )}

        {/* ---------- Chat ---------- */}
        {!panelOpen && (
          <>
            <div className="vitstra-ai-body" ref={containerRef}>
              <div className="messages-wrapper">
                <div className="messages-list">
                  {isLoadingCategories ? null : messages.length === 0 ? (
                    <div className="welcome-message">
                      <p className="welcome-text">Ask me to customize your feed.</p>
                      <div className="category-buttons-grid">
                        {popularCategories
                          .filter(cat => availableCategories.includes(cat))
                          .map(cat => (
                            <button
                              key={cat}
                              className="category-quick-btn"
                              onClick={() => window.handleTagClick(cat)}
                            >
                              {cat === "ai" ? "AI" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </button>
                          ))}
                      </div>
                    </div>
                  ) : (
                    messages.map(m => (
                      <MessageBubble
                        key={m.id}
                        message={m}
                        onFeedback={handleFeedback}
                        goToVideos={goToVideos}
                        onContextMenu={handleContextMenu}
                        isEditing={editingId === m.id}
                        showFeedbackButton={m.showFeedbackBtn}
                        startEdit={startEdit}
                        copyMessage={copyMessage}
                        isTransitioning={transitioningAiId === m.id}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* ---------- Input + Action Buttons ---------- */}
            <div className="input-orb-container">
              <div className="action-buttons-row">
                <button className="action-btn videos-btn" onClick={goToVideos}>
                  <FaVideo className="footer-icon" /> See Videos
                </button>
                <button className="action-btn shorts-btn" onClick={goToShorts}>
                  <FaPlayCircle className="footer-icon" /> See Shorts
                </button>
              </div>
              <form className="input-form" onSubmit={handleSubmit}>
                <textarea
                  ref={inputRef}
                  className="orb-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  placeholder={
                    editingId
                      ? "Edit..."
                      : inputFocused
                      ? "Ask me anything"
                      : PLACEHOLDERS[placeholderIdx]
                  }
                  rows={1}
                />
                <button
                  className={`send-btn ${
                    editingId ? "editing" : isStreaming ? "streaming" : ""
                  }`}
                  type={isStreaming ? "button" : "submit"}
                  onClick={isStreaming ? stopStreaming : undefined}
                  disabled={
                    isStreaming
                      ? false
                      : editingId
                      ? !input.trim()
                      : !deferredInput.trim() || isLoadingCategories
                  }
                >
                  {isStreaming ? (
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <rect x="4" y="4" width="16" height="16" rx="2" />
                    </svg>
                  ) : editingId ? (
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path
                        d="M2 21L22 12L2 3V10L16 12L2 14V21Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </button>
                {editingId && (
                  <button
                    type="button"
                    className="cancel-edit-btn"
                    onClick={cancelEdit}
                  >
                    Cancel
                  </button>
                )}
              </form>

              {/* floating feed button */}
              {showFeedButton && (
                <button
                  className={`action-btn floating-feed-btn ${
                    showScrollBtn || showFeedButton ? "visible" : ""
                  }`}
                  onClick={goToVideos}
                >
                  See the feed
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* ---------- Context menu ---------- */}
      {contextMenu.show && (
        <div
          className="msg-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {contextMenu.isUser && (
            <button
              onClick={() => startEdit(contextMenu.msgId, contextMenu.text)}
            >
              Edit
            </button>
          )}
          <button onClick={() => copyMessage(contextMenu.text)}>Copy</button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Reusable components */
/* ------------------------------------------------------------------ */
const SavedBtn = memo(({ label, active, onClick }) => (
  <button className={`saved-btn ${active ? "active" : ""}`} onClick={onClick}>
    {label}
  </button>
));

/* MessageBubble */
const MessageBubble = memo(
  ({
    message,
    onFeedback,
    showFeedbackButton,
    startEdit,
    copyMessage,
    onContextMenu,
    isTransitioning,
  }) => {
    const isUser = message.from === "user";
    const [isMuted, setIsMuted] = useState(true);
    const iframeRef = useRef(null);

    const toggleMute = useCallback(() => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      const command = isMuted ? "unMute" : "mute";
      iframe.contentWindow.postMessage(
        `{"event":"command","func":"${command}","args":""}`,
        "*"
      );
      setIsMuted(prev => !prev);
    }, [isMuted]);

    const mdComponents = {
      table: ({ children }) => (
        <table className="ai-markdown-table">{children}</table>
      ),
    };

    return (
      <div
        className={`msg ${isUser ? "user" : "ai"} ${
          message.isStreaming ? "streaming" : ""
        }`}
        onContextMenu={e => onContextMenu(e, message)}
      >
        <div className="bubble">
          {isUser ? (
            <div className="user-content">{message.text}</div>
          ) : (
            <div
              className={`ai-content markdown-body ${
                message.isThinking ? "thinking" : ""
              }`}
            >
              {message.isThinking ? (
                <div className="typing-indicator">
                  <div className="dot" />
                  <div className="dot" />
                  <div className="dot" />
                </div>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={mdComponents}
                >
                  {message.text}
                </ReactMarkdown>
              )}

              {/* Video (if any) */}
              {message.videoId && (
                <div className="video-box-tall">
                  <div className="video-wrapper-tall">
                    <iframe
                      ref={iframeRef}
                      src={`https://www.youtube.com/embed/${message.videoId}?autoplay=1&mute=1&loop=1&playlist=${message.videoId}&enablejsapi=1&rel=0&modestbranding=1&iv_load_policy=3`}
                      allow="autoplay; fullscreen"
                      title={`short-${message.videoId}`}
                      className="youtube-iframe-tall"
                    />
                  </div>
                  <button
                    className={`unmute-btn ${!isMuted ? "active" : ""}`}
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 5L6 9H3v6h3l5 4V5z" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 5L6 9H3v6h3l5 4V5z" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                      </svg>
                    )}
                  </button>
                </div>
              )}

              {isTransitioning && <div className="shimmer-overlay" />}
            </div>
          )}

          {showFeedbackButton && !isUser && (
            <div className="feedback-btn-wrapper">
              <button className="feedback-btn" onClick={onFeedback}>
                Give Feedback
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export function VitStraAIWithProvider() {
  return <VitStraAI />;
}